import { useRef, useEffect } from 'react';
import usePrevious from './usePrevious';

/**
 * Given the current ordered list of element IDs and the ID of the element being deleted,
 * returns the ID of the element that should receive focus after removal.
 */
export function computeFocusTarget(itemIds, deletingId, fallbackFocusId, focusNextOnFirst = true) {
  if (!Array.isArray(itemIds) || !deletingId) {
    return fallbackFocusId;
  }

  const idx = itemIds.indexOf(deletingId);

  if (idx === -1) {
    return fallbackFocusId;
  }

  const prevId = idx > 0 ? itemIds[idx - 1] : null;
  const nextId = itemIds[idx + 1];

  // Prefer previous item; if deleting the first one, pick next
  return prevId ?? (focusNextOnFirst ? nextId : null) ?? fallbackFocusId;
}

/**
 * Focuses an element by ID, falling back to a secondary element if the primary is not found.
 */
export function focusElementById(targetId, fallbackId) {
  const el = document.getElementById(targetId) || (fallbackId ? document.getElementById(fallbackId) : null);

  if (el && typeof el.focus === 'function') {
    el.focus();
  }
}

/**
 * Manages focus after an item is deleted from a list.
 *
 * Strategy:
 *  1. Try to move focus to the **previous** item's target element by default.
 *  2. If no items remain, move focus to `fallbackFocusId`.
 *
 * @param {Object} config
 * @param {string[]} config.itemIds       Flat, ordered list of focusable DOM element IDs (e.g. delete button IDs).
 * @param {string|null} config.deletingId The DOM element ID of the item currently being deleted, or null.
 * @param {boolean} config.isDeleting     `true` while the delete operation is in progress.
 * @param {string} config.fallbackFocusId DOM ID of the element to focus when the list becomes empty.
 * @param {boolean} config.focusNextOnFirst Whether to focus the next item when the deleted item has no previous item.
 */
export default function useDeleteFocusManager({ itemIds, deletingId, isDeleting, fallbackFocusId, focusNextOnFirst = true }) {
  const prevIsDeleting = usePrevious(isDeleting);
  const focusTargetIdRef = useRef(null);
  const awaitingDomUpdateRef = useRef(false);

  useEffect(() => {
    if (prevIsDeleting === false && isDeleting === true) {
      // Phase 1: delete just started — compute where focus should go after the item is removed.
      focusTargetIdRef.current = computeFocusTarget(itemIds, deletingId, fallbackFocusId, focusNextOnFirst);
      awaitingDomUpdateRef.current = true;
    } else if (prevIsDeleting === true && isDeleting === false && awaitingDomUpdateRef.current) {
      // Phase 2: delete finished, DOM will update — move focus.
      const targetId = focusTargetIdRef.current || fallbackFocusId;
      focusTargetIdRef.current = null;

      window.requestAnimationFrame(() => {
        focusElementById(targetId, fallbackFocusId);
        awaitingDomUpdateRef.current = false;
      });
    }
  }, [isDeleting]); // eslint-disable-line react-hooks/exhaustive-deps
}
